import React, { useContext, useState, useEffect } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Settings, Eye } from "lucide-react";
import ObjectivesSelect from "@/components/dashboard/roadmap/ObjectivesSelect";
import VisionField from "@/components/dashboard/roadmap/VisionField";
import CapacitySelect from "@/components/dashboard/roadmap/CapacitySelect";
import FeedbackFocusSelect from "@/components/dashboard/roadmap/FeedbackFocusSelect";
import DevToolSelect, { DevTool } from "@/components/dashboard/roadmap/DevToolSelect";
import { FeedbackFocus } from "@/hooks/useProductConfig";
import { RoadmapContext } from "@/context/RoadmapContext";

interface RoadmapConfigurationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: string;
  productName: string;
}

const RoadmapConfigurationDialog = ({
  open,
  onOpenChange,
  productId,
  productName
}: RoadmapConfigurationDialogProps) => {
  const { selectedProduct, changeProductConfigs } = useContext(RoadmapContext)
  const [missionVision, setMissionVision] = useState('')
  const [selectedCapacity, setSelectedCapacity] = useState('3')
  const [selectedDevTool, setSelectedDevTool] = useState('jira')
  const [selectedFeedbackFocus, setSelectedFeedbackFocus] = useState('all-time')
  const [selectedObjectives, setSelectedObjectives] = useState([])

  useEffect(() => {
    setSelectedCapacity(selectedProduct.configs?.capacity || '3')
    setSelectedDevTool(selectedProduct.configs?.dev_tool || 'jira')
    setSelectedFeedbackFocus(selectedProduct.configs?.feedback_focus || 'all-time')
    setSelectedObjectives(selectedProduct.configs?.objectives || [])
    setMissionVision(selectedProduct.configs?.mission_vision || '')
  }, [selectedProduct])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-bold">
            <Settings className="h-5 w-5 text-[#FF4A8E]" />
            {productName} Configuration
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            Configure your product settings to optimize your roadmap.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-6 py-4">
          <VisionField 
            value={missionVision} 
            onChange={setMissionVision} 
          />
          
          <div className="grid grid-cols-2 gap-6">
            <CapacitySelect 
              value={selectedCapacity} 
              onChange={setSelectedCapacity} 
            />
            
            <DevToolSelect 
              value={selectedDevTool as DevTool}
              onChange={setSelectedDevTool} 
            />
          </div>
          
          <div className="grid grid-cols-2 gap-6">
            <ObjectivesSelect 
              selectedObjectives={selectedObjectives}
              setSelectedObjectives={setSelectedObjectives}
            />
            
            <FeedbackFocusSelect
              value={selectedFeedbackFocus as FeedbackFocus}
              onChange={setSelectedFeedbackFocus}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button
            className="bg-[#FF4A8E] text-white hover:bg-[#FF4A8E]/90"
            onClick={() => {
              changeProductConfigs(productId, {
                capacity: selectedCapacity,
                dev_tool: selectedDevTool,
                feedback_focus: selectedFeedbackFocus,
                objectives: selectedObjectives,
                mission_vision: missionVision,
              });
              onOpenChange(false);
            }}
          >
            Apply Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RoadmapConfigurationDialog;
